import { useState } from "react";
import { toast } from "sonner";
import { helpAPI } from "../../lib/api";

const FAQItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  const ACCENT = "#2563EB";
  const SURFACE = "#0A0D13";
  const BORDER = "#1E2433";
  const TEXT_PRIMARY = "#F9FAFB";
  const TEXT_MUTED = "#6B7280";
  const TEXT_DIM = "#374151";
  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 9, marginBottom: 8, overflow: "hidden" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", background: "none", border: "none", cursor: "pointer",
        padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>{faq.q}</span>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
      {open && (
        <div style={{ padding: "0 18px 14px", borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
          <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.7 }}>{faq.a}</p>
        </div>
      )}
    </div>
  );
};

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);

const MiniBar = ({ value, max, color = ACCENT }) => (
  <div style={{ flex: 1, height: 5, background: BORDER, borderRadius: 3, overflow: "hidden" }}>
    <div style={{ width: `${(value / max) * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
  </div>
);

/* ── Stat card ── */
const StatCard = ({ label, value, sub, delta, color = TEXT_PRIMARY, deltaColor }) => (
  <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px" }}>
    <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 8 }}>{label.toUpperCase()}</div>
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 26, fontWeight: 700, color, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.025em" }}>{value}</span>
      {delta && <span style={{ fontSize: 12, color: deltaColor || "#22C55E", fontFamily: "'DM Mono', monospace" }}>{delta}</span>}
    </div>
    {sub && <div style={{ fontSize: 11, color: TEXT_DIM }}>{sub}</div>}
  </div>
);

/* ── Mini sparkline (pure CSS) ── */
const Sparkline = ({ data, color = ACCENT }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const h = 36;
  const w = 120;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

/* ── Shadow Mode Experiment Card ── */
const SuggestionCard = ({ s, onApply }) => {
  const statusColor = {
    not_started: TEXT_DIM, running: "#F59E0B", passed: "#22C55E", failed: "#EF4444"
  }[s.shadow_status] || TEXT_DIM;

  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap", alignItems: "center" }}>
            <Badge label={s.category.toUpperCase()} color={ACCENT} />
            <Badge label={`SHADOW: ${s.shadow_status.replace("_", " ").toUpperCase()}`} color={statusColor} />
            {s.apply_ready && <Badge label="READY TO APPLY" color="#22C55E" />}
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4, fontFamily: "'DM Sans', sans-serif" }}>{s.hypothesis}</h3>
          <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6 }}>{s.proposed_change}</p>
        </div>
      </div>

      {/* Metrics */}
      {s.metrics && (
        <div style={{ display: "flex", gap: 16, padding: "10px 14px", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 12 }}>
          {s.metrics.map((m, i) => (
            <div key={i}>
              <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 3 }}>{m.label.toUpperCase()}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>{m.before}</span>
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                <span style={{ fontSize: 13, fontWeight: 600, color: m.better ? "#22C55E" : "#EF4444" }}>{m.after}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        {s.apply_ready && (
          <button onClick={() => onApply(s.id)} style={{
            background: "#052E16", border: "1px solid #166534", borderRadius: 7,
            padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#22C55E",
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>Apply Change →</button>
        )}
        {!s.apply_ready && s.shadow_status === "not_started" && (
          <button style={{
            background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 7,
            padding: "8px 16px", fontSize: 12, fontWeight: 600, color: ACCENT,
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>Start Shadow Mode</button>
        )}
        <button style={{
          background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 7,
          padding: "8px 14px", fontSize: 12, color: TEXT_DIM, cursor: "pointer",
        }}>Reject</button>
        <button style={{
          background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 7,
          padding: "8px 14px", fontSize: 12, color: TEXT_DIM, cursor: "pointer",
        }}>View Details</button>
      </div>
    </div>
  );
};

/* ── DATA ── */
const TAB_DATA = [
  { tab: "Overview", views: 412, avgDuration: "2m 14s", pct: 100 },
  { tab: "Signals", views: 389, avgDuration: "1m 48s", pct: 94 },
  { tab: "MEDDICC", views: 341, avgDuration: "3m 02s", pct: 83 },
  { tab: "Hypotheses", views: 298, avgDuration: "2m 31s", pct: 72 },
  { tab: "Buying Reality", views: 271, avgDuration: "1m 56s", pct: 66 },
  { tab: "Brief", views: 198, avgDuration: "0m 54s", pct: 48 },
  { tab: "Traffic", views: 156, avgDuration: "0m 41s", pct: 38 },
  { tab: "Articles", views: 132, avgDuration: "1m 12s", pct: 32 },
];

const SUGGESTIONS = [
  {
    id: 1, category: "UX", apply_ready: true,
    shadow_status: "passed",
    hypothesis: "Users are dropping off at the MEDDICC tab because unknown fields aren't visually distinct enough.",
    proposed_change: "Add a distinct 'gap' badge with amber colour to all MEDDICC unknown fields and surface recommended questions more prominently above the fold.",
    metrics: [
      { label: "MEDDICC Time on Tab", before: "1m 22s", after: "3m 02s", better: true },
      { label: "Recommended Q Clicks", before: "4%", after: "31%", better: true },
    ],
  },
  {
    id: 2, category: "Scoring", apply_ready: false,
    shadow_status: "running",
    hypothesis: "Fit score confidence labels (High/Medium/Low) are being ignored by users — they rely on the numeric score only.",
    proposed_change: "Replace text confidence labels with a compound score display showing numeric + colour band + confidence percentage inline.",
    metrics: null,
  },
  {
    id: 3, category: "Model Quality", apply_ready: false,
    shadow_status: "not_started",
    hypothesis: "Payments detection is producing false negatives for embedded checkouts using custom JS frameworks.",
    proposed_change: "Add headless DOM traversal pass for React/Vue SPA checkout detection, increasing BNPL coverage by estimated 18%.",
    metrics: null,
  },
];

const SYSTEM_HEALTH = [
  { label: "Scan Success Rate", value: "94.2%", trend: "+1.3%", color: "#22C55E", data: [90, 91, 93, 92, 94, 93, 94] },
  { label: "Avg Run Time", value: "38s", trend: "-4s", color: "#22C55E", data: [52, 50, 48, 44, 42, 40, 38] },
  { label: "Low Conf Rate", value: "12.4%", trend: "-2.1%", color: "#F59E0B", data: [18, 17, 16, 15, 14, 13, 12] },
  { label: "Evidence Capture", value: "97.8%", trend: "+0.4%", color: "#22C55E", data: [95, 96, 96, 97, 97, 98, 98] },
];

/* ═══════════════════════ GET HELP ═══════════════════════ */
const GetHelp = () => {
  const [query, setQuery] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);

  const FAQS = [
    { q: "How does identity resolution work?", a: "Inference cross-references your input name and domain against registry databases to find the canonical entity. Confidence is scored 0–100 based on match quality." },
    { q: "What does 'material change' mean?", a: "A material change is a delta that Inference judges to be strategically significant — e.g. a new payment provider added, BNPL positioning shifted, or checkout type changed." },
    { q: "Can I override a fit score?", a: "Yes. In the Results → Overview tab, there is an Override button. Overrides require a reason and are logged in the audit trail. They do not alter the underlying scoring logic." },
    { q: "How do I export a brief?", a: "In Results → Brief tab, select which modules to include, then use the Export panel to download as PDF, Markdown, or JSON, or copy to clipboard." },
    { q: "What is MEDDICC gap tracking?", a: "MEDDICC maps known, assumed, and unknown intelligence across Metrics, Economic Buyer, Decision Criteria, Decision Process, Identify Pain, Champion, and Competition. Gaps are fields marked unknown with recommended next questions." },
    { q: "How does monitoring work?", a: "Add a company to monitoring from its Results page. Inference re-scans at your configured cadence (daily/weekly), produces a delta summary, and flags material vs minor changes in your notification feed." },
  ];

  const handleAsk = async () => {
    const q = query.trim();
    if (!q || asking) return;
    setAsking(true);
    setAnswer(null);
    try {
      const res = await helpAPI.ask(q, history);
      const text = res?.data?.answer || "";
      setAnswer(text);
      setHistory(prev => [...prev, { question: q, answer: text }].slice(-3));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not get an answer right now. Please try again.");
    } finally {
      setAsking(false);
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 28px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>
            How can we help?
          </h1>
          <p style={{ fontSize: 14, color: TEXT_MUTED }}>Search docs, ask an AI assistant, or browse FAQs below.</p>
        </div>

        {/* AI assistant */}
        <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px", marginBottom: 32 }}>
          <div style={{ fontSize: 11, color: ACCENT, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 10 }}>AI SUPPORT ASSISTANT</div>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              placeholder="Ask anything about Inference…"
              data-testid="help-ask-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAsk()}
              style={{
                flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "11px 14px", fontSize: 14, color: TEXT_PRIMARY, outline: "none",
                fontFamily: "'DM Sans', sans-serif",
              }}
              onFocus={e => e.target.style.borderColor = ACCENT}
              onBlur={e => e.target.style.borderColor = BORDER}
            />
            <button onClick={handleAsk} data-testid="help-ask-btn" disabled={asking} style={{
              background: asking ? "#1E3A6E" : ACCENT, color: asking ? "#6B9AE0" : "#fff",
              border: "none", borderRadius: 8, padding: "11px 20px",
              fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s",
            }}>
              {asking && <div style={{ width: 12, height: 12, border: "2px solid #6B9AE0", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />}
              {asking ? "Thinking…" : "Ask →"}
            </button>
          </div>
          {answer && (
            <div data-testid="help-answer" style={{ marginTop: 16, padding: "14px 16px", background: `${ACCENT}08`, border: `1px solid ${ACCENT}22`, borderRadius: 8 }}>
              <div style={{ fontSize: 10, color: ACCENT, fontFamily: "'DM Mono', monospace", marginBottom: 8 }}>ANSWER</div>
              <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{answer}</p>
            </div>
          )}
        </div>

        {/* FAQs */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 14 }}>FREQUENTLY ASKED QUESTIONS</div>
          {FAQS.map((faq, i) => (
            <FAQItem key={i} faq={faq} />
          ))}
        </div>

        {/* Links */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          {[
            { title: "Documentation", desc: "Full feature documentation and API reference.", icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" },
            { title: "Changelog", desc: "What's new and what's changed in Inference.", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
            { title: "Contact Support", desc: "Reach the team for account or technical issues.", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
          ].map((l, i) => (
            <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px", cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#2D3748"}
              onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}
            >
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={1.8} style={{ marginBottom: 10 }}><path d={l.icon} /></svg>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>{l.title}</div>
              <div style={{ fontSize: 11, color: TEXT_DIM }}>{l.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════ PRODUCT DASHBOARD ═══════════════════════ */
const ProductDashboard = () => {
  const [activeSection, setActiveSection] = useState("usage");
  const [appliedSuggestions, setAppliedSuggestions] = useState(new Set());

  const applyS = id => setAppliedSuggestions(s => new Set([...s, id]));

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      {/* Section tabs */}
      <div style={{ display: "flex", gap: 6, padding: "16px 28px 0" }}>
        {["usage", "health", "suggestions"].map(s => (
          <button key={s} onClick={() => setActiveSection(s)} style={{
            fontSize: 12, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
            border: `1px solid ${activeSection === s ? ACCENT : BORDER}`,
            background: activeSection === s ? `${ACCENT}18` : "transparent",
            color: activeSection === s ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
            textTransform: "capitalize",
          }}>{s}</button>
        ))}
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>

        {/* ── USAGE ── */}
        {activeSection === "usage" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
              <StatCard label="Total Analyses" value="1,284" delta="+142 this week" sub="Across all workspaces" />
              <StatCard label="Avg Time to Output" value="38s" delta="-6s vs last week" sub="From scan start" />
              <StatCard label="Export Rate" value="34%" delta="+4%" sub="Analyses that generated export" />
              <StatCard label="Fit Override Rate" value="8.2%" delta="-1.1%" sub="Of completed analyses" color="#F59E0B" deltaColor="#22C55E" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {/* Tab usage */}
              <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "18px 20px" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>Tab Usage</div>
                <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 16 }}>Views + average time on tab</div>
                {TAB_DATA.map((t, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>{t.tab}</span>
                      <div style={{ display: "flex", gap: 12 }}>
                        <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{t.avgDuration}</span>
                        <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace", minWidth: 36, textAlign: "right" }}>{t.views}</span>
                      </div>
                    </div>
                    <MiniBar value={t.pct} max={100} color={ACCENT} />
                  </div>
                ))}
              </div>

              {/* Fit overrides + dropoff */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "18px 20px", flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>Fit Override Reasons</div>
                  <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 14 }}>Top reasons users manually override fit score</div>
                  {[
                    { reason: "Strategic account — override to Tier 1", pct: 44 },
                    { reason: "Industry mismatch correction", pct: 28 },
                    { reason: "Missing firmographic data", pct: 18 },
                    { reason: "Persona doesn't match signal", pct: 10 },
                  ].map((r, i) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: TEXT_MUTED }}>{r.reason}</span>
                        <span style={{ fontSize: 11, color: "#F59E0B", fontFamily: "'DM Mono', monospace" }}>{r.pct}%</span>
                      </div>
                      <MiniBar value={r.pct} max={100} color="#F59E0B" />
                    </div>
                  ))}
                </div>

                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "18px 20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 14 }}>Funnel Drop-off</div>
                  {[
                    { step: "Analysis Started", n: 1284, pct: 100 },
                    { step: "Identity Confirmed", n: 1198, pct: 93 },
                    { step: "Processing Completed", n: 1147, pct: 89 },
                    { step: "Results Opened", n: 1021, pct: 80 },
                    { step: "Export Generated", n: 436, pct: 34 },
                  ].map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, color: TEXT_MUTED, minWidth: 160 }}>{s.step}</span>
                      <MiniBar value={s.pct} max={100} color={s.pct > 70 ? ACCENT : s.pct > 40 ? "#F59E0B" : "#EF4444"} />
                      <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", minWidth: 36 }}>{s.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── HEALTH ── */}
        {activeSection === "health" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
              {SYSTEM_HEALTH.map((h, i) => (
                <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px" }}>
                  <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 8 }}>{h.label.toUpperCase()}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: h.color, fontFamily: "'DM Sans', sans-serif" }}>{h.value}</span>
                    <span style={{ fontSize: 11, color: "#22C55E", fontFamily: "'DM Mono', monospace" }}>{h.trend}</span>
                  </div>
                  <Sparkline data={h.data} color={h.color} />
                </div>
              ))}
            </div>

            <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "18px 20px" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 16 }}>Module Accuracy</div>
              {[
                { module: "Payments Detection", acc: 94, scans: 1284 },
                { module: "BNPL Detection", acc: 89, scans: 1284 },
                { module: "Identity Resolution", acc: 97, scans: 1284 },
                { module: "Classification", acc: 88, scans: 1147 },
                { module: "MEDDICC Extraction", acc: 81, scans: 1021 },
                { module: "Traffic Estimation", acc: 72, scans: 689 },
              ].map((m, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: TEXT_PRIMARY, minWidth: 180 }}>{m.module}</span>
                  <MiniBar value={m.acc} max={100} color={m.acc >= 90 ? "#22C55E" : m.acc >= 80 ? "#F59E0B" : "#EF4444"} />
                  <span style={{ fontSize: 12, color: m.acc >= 90 ? "#22C55E" : m.acc >= 80 ? "#F59E0B" : "#EF4444", fontFamily: "'DM Mono', monospace", minWidth: 40 }}>{m.acc}%</span>
                  <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{m.scans.toLocaleString()} scans</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SUGGESTIONS ── */}
        {activeSection === "suggestions" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 6 }}>Improvement Suggestions</h2>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>AI-generated hypotheses based on usage and system metrics. Each can be tested in shadow mode before applying.</p>
            </div>
            {SUGGESTIONS.map(s => (
              appliedSuggestions.has(s.id) ? (
                <div key={s.id} style={{ background: "#052E16", border: "1px solid #166534", borderRadius: 10, padding: "14px 18px", marginBottom: 10, display: "flex", gap: 10, alignItems: "center" }}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth={2}><polyline points="20 6 9 17 4 12" /></svg>
                  <span style={{ fontSize: 13, color: "#22C55E" }}>Change applied: <strong>{s.hypothesis.slice(0, 60)}…</strong></span>
                </div>
              ) : <SuggestionCard key={s.id} s={s} onApply={applyS} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── ROOT: Switcher between both screens ── */
export default function HelpScreen() {
  const [screen, setScreen] = useState("dashboard");

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #374151; }
        button { outline: none; }
      `}</style>

      {screen === "dashboard" && <ProductDashboard />}
      {screen === "help" && <GetHelp />}

      {/* Switcher */}
      <div style={{
        position: "fixed", bottom: 20, right: 20,
        background: SURFACE_2, border: `1px solid ${BORDER}`,
        borderRadius: 100, padding: "6px 8px",
        display: "flex", gap: 4, zIndex: 999,
      }}>
        {[["dashboard", "Product Dashboard"], ["help", "Get Help"]].map(([id, label]) => (
          <button key={id} onClick={() => setScreen(id)} style={{
            padding: "5px 14px", borderRadius: 100, border: "none",
            background: screen === id ? ACCENT : "transparent",
            color: screen === id ? "#fff" : TEXT_MUTED,
            fontSize: 11, fontWeight: 500, cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s",
          }}>{label}</button>
        ))}
      </div>
    </div>
  );
}
